import { InternalParserError } from "./errors";

export const DEFAULT_DOCUMENT_PROFILE = "D00000000001";

export enum SchemaElementArt {
  Abstrakt = "ABS",
  Harmonisiert = "HAR",
  Rechtsnormgebunden = "RNG",
}

export function parseSchemaElementArt(value: string): SchemaElementArt {
  switch (value) {
    case "ABS":
      return SchemaElementArt.Abstrakt;
    case "HAR":
      return SchemaElementArt.Harmonisiert;
    case "RNG":
      return SchemaElementArt.Rechtsnormgebunden;
    default:
      throw new InternalParserError(
        `Invalid value for SchemaElementArt: ${value}`,
      );
  }
}

// See: https://www.xrepository.de/details/urn:xoev-de:xprozess:codeliste:status
// Version: 2022-07-12
export enum FreigabeStatus {
  InPlanung = "1",
  InBearbeitung = "2",
  Entwurf = "3",
  MethodischFreigegeben = "4",
  FachlichFreigegebenSilber = "5",
  FachlichFreigegebenGold = "6",
  Inaktiv = "7",
  VorgesehenZumLoeschen = "8",
}

export function parseFreigabeStatus(value: string): FreigabeStatus {
  switch (value) {
    case "1":
      return FreigabeStatus.InPlanung;
    case "2":
      return FreigabeStatus.InBearbeitung;
    case "3":
      return FreigabeStatus.Entwurf;
    case "4":
      return FreigabeStatus.MethodischFreigegeben;
    case "5":
      return FreigabeStatus.FachlichFreigegebenSilber;
    case "6":
      return FreigabeStatus.FachlichFreigegebenGold;
    case "7":
      return FreigabeStatus.Inaktiv;
    case "8":
      return FreigabeStatus.VorgesehenZumLoeschen;
    default:
      throw new InternalParserError(`Invalid FreigabeStatus: ${value}`);
  }
}

export enum AbleitungsmodifikationenStruktur {
  AllesModifizierbar = "0",
  NurErweiterbar = "1",
  NurEinschraenkbar = "2",
  NichtModifizierbar = "3",
}

export function parseAbleitungsmodifikationenStruktur(
  value: string,
): AbleitungsmodifikationenStruktur {
  switch (value) {
    case "0":
      return AbleitungsmodifikationenStruktur.AllesModifizierbar;
    case "1":
      return AbleitungsmodifikationenStruktur.NurErweiterbar;
    case "2":
      return AbleitungsmodifikationenStruktur.NurEinschraenkbar;
    case "3":
      return AbleitungsmodifikationenStruktur.NichtModifizierbar;
    default:
      throw new InternalParserError(
        `Invalid AbleitungsmodifikationenStruktur: ${value}`,
      );
  }
}

export enum AbleitungsmodifikationenRepraesentation {
  NichtModifizierbar = "0",
  Modifizierbar = "1",
}

export function parseAbleitungsmodifikationenRepraesentation(
  value: string,
): AbleitungsmodifikationenRepraesentation {
  switch (value) {
    case "0":
      return AbleitungsmodifikationenRepraesentation.NichtModifizierbar;
    case "1":
      return AbleitungsmodifikationenRepraesentation.Modifizierbar;
    default:
      throw new InternalParserError(
        `Invalid AbleitungsmodifikationenRepraesentation: ${value}`,
      );
  }
}

export type Anzahl =
  | {
      type: "value";
      optional: boolean;
    }
  | {
      type: "array";
      minSize: number;
      maxSize?: number;
    };

const AnzahlPattern = /\d+:(\*|\d+)/;

export function parseAnzahl(value: string): Anzahl {
  if (!AnzahlPattern.test(value)) {
    throw new InternalParserError(`Invalid Anzahl: ${value}`);
  }

  const [min, max] = value.split(":");

  const minSize = Number.parseInt(min);
  if (max === "*") {
    return { type: "array", minSize };
  } else {
    const maxSize = Number.parseInt(max);
    if (maxSize < minSize) {
      throw new InternalParserError(`Invalid Anzahl: ${value}`);
    }

    if (maxSize > 1) {
      return { type: "array", minSize, maxSize };
    } else {
      return { type: "value", optional: minSize === 0 };
    }
  }
}

export function serializeAnzahl(anzahl: Anzahl): string {
  if (anzahl.type === "value") {
    return `${anzahl.optional ? 0 : 1}:1`;
  } else if (anzahl.maxSize) {
    return `${anzahl.minSize}:${anzahl.maxSize}`;
  } else {
    return `${anzahl.minSize}:*`;
  }
}

export function parseXmlDocument(data: string): Document {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(data, "text/xml");

  // Check for parsing errors
  const parserError = xmlDoc.querySelector("parsererror");
  if (parserError) {
    throw new InternalParserError(
      parserError.textContent || "XML parsing error",
    );
  }

  return xmlDoc;
}
